import Title from "common/Typography/Title";
import styled, { css } from "styled-components";
import Text from "common/Typography/Text";
import Card from "common/Boxes/Card";
import { PairButton } from "features/bots/BotForm/General/PairButtons.tsx";
import { formPanel } from "features/bots/BotForm/NewLook/styles";
import {
  getNewLookTokens,
  NEW_LOOK_RADIUS,
  newLookPhoneType,
  newLookType,
  getPanelShadow,
} from "common/NewLook/tokens.ts";
import { phoneMaxWidth } from "common/NewLook/layout.ts";

export const SectionTitle = styled(Title).attrs({
  level: 3,
})`
  ${(p) =>
    p.theme.isNewLook &&
    `
    &.ant-typography {
      margin: 0;
      color: ${p.theme.colors.titleColor};
      font-size: 14px;
      line-height: 20px;
      font-weight: 600;
      letter-spacing: -0.005em;
    }
  `}
  @media (max-width: 400px) {
    font-size: 14px;
  }
  /* Phone (new look): the section role, 17 / 500. */
  ${(p) =>
    p.theme.isNewLook &&
    css`
      @media (max-width: ${phoneMaxWidth}) {
        &.ant-typography {
          ${newLookPhoneType.section};
        }
      }
    `}
`;

export const paddingStyles = css`
  padding: 20px;
  @media (max-width: 500px) {
    padding: 12px;
  }
`;

export const WarningWrapper = styled(Text).attrs({
  as: "div",
})`
  margin: 8px 0;
  display: flex;
  align-items: center;
  column-gap: 12px;

  span {
    font-size: 22px;
  }

  font-size: ${(p) => p.theme.typography.fontSizes.S};
`;

export const LeverageTrailingText = styled(Text)`
  font-size: ${(p) => p.theme.typography.fontSizes.S};
  color: ${(p) => p.theme.colors.midDark};
  margin-top: 8px;
`;

export const BacktestingStatisticCard = styled(Card)<{ $isMobile?: boolean }>`
  padding: ${(p) => (p.$isMobile ? 16 : 20)}px;
  ${(p) =>
    p.theme.isNewLook &&
    css`
      padding: 16px;
      border-radius: ${NEW_LOOK_RADIUS.panel}px;
      box-shadow: ${getPanelShadow(p.theme)};
    `};
`;

/** New look: the title row of a statistics card. */
export const NewLookStatisticTitle = styled.div`
  display: flex;
  align-items: center;
  margin-bottom: 12px;
  ${newLookType.panelTitle};
  color: ${(p) => getNewLookTokens(p.theme).ink};
`;

export const BotFormItemWrapper = styled.div<{
  $isChartLayout?: boolean;
  $isMobile?: boolean;
  $summaryTabsSmallScreenLayout?: boolean;
}>`
  overflow-y: hidden;
  border-radius: 8px;
  border: ${(p) => p.theme.borders.medium};
  flex: ${(p) => (p.$isChartLayout ? 0 : 1)} 1 auto;
  ${(p) => !p.$isChartLayout && `height: fit-content`};
  ${(p) => p.$isMobile && `padding-bottom: 160px;`};
  background: ${(p) => p.theme.colors.cardBgColor};
  ${(p) =>
    p.$summaryTabsSmallScreenLayout &&
    `
    overflow-y: unset;
  `};
  ${(p) => p.theme.isNewLook && formPanel};
`;

const MOBILE_FORM_MAX_WIDTH = 500;

export const FormAndSummaryWrapper = styled.div<{
  $isMobile?: boolean;
  $isChartLayout?: boolean;
}>`
  display: flex;
  ${(p) =>
    p.$isChartLayout
      ? `
    flex-direction: column;
    width: 365px;
    height: 100%;
  `
      : `
    max-width: 1175px;
    width: 100%;
    margin: 0 auto;
    column-gap: 20px;
  `};
  ${(p) =>
    p.$isMobile &&
    `
    width: 100%; 
    max-width: ${MOBILE_FORM_MAX_WIDTH}px;
    height: fit-content;
  `};
`;

export const SummaryInnerWrapper = styled.div<{
  $isMobile?: boolean;
  $pagePadding: number;
  $isChartLayout?: boolean;
  $bordered?: boolean;
  $showBotSummaryTabs?: boolean;
  $isBottomNavigator?: boolean;
}>`
  border-radius: ${(p) => (p.$isBottomNavigator ? 0 : 8)}px;
  z-index: 999;
  ${(p) =>
    p.$bordered &&
    `
    border: ${p.theme.borders.medium};
    padding: 12px;
  `};
  ${(p) =>
    p.$isMobile
      ? `
    padding: 6px 12px 12px 12px;
    border: ${p.$isBottomNavigator ? "none" : p.theme.borders.medium};
    margin: 0 0 16px 0;
    width: calc(100% - ${2 * (p.$pagePadding || 0) + 5}px);
    background: ${p.theme.colors.layoutBgColor};
    max-width: ${MOBILE_FORM_MAX_WIDTH}px;
  `
      : `
      margin-top: ${p.$isChartLayout ? 20 : 0}px;
  `};
  ${(p) =>
    p.$showBotSummaryTabs &&
    `
    margin-bottom: 20px;
  `}
  ${(p) =>
    p.$isBottomNavigator &&
    `
    border-top: ${p.theme.borders.medium};
    width: 100%;
    margin: 0;
  `};
  ${(p) =>
    (p.$isChartLayout || p.$isMobile) &&
    !p.$isBottomNavigator &&
    `
    background: ${p.theme.colors.cardBgColor};
  `};
  ${(p) =>
    p.theme.isNewLook &&
    p.$bordered &&
    !p.$isBottomNavigator &&
    css`
      ${formPanel};
      padding: 16px;
    `};
  ${(p) =>
    p.theme.isNewLook &&
    p.$isBottomNavigator &&
    css`
      /* 12px above the status line, between it and the actions, and below. */
      padding: 12px 16px;
      border-top: ${p.theme.borders.lightest};
      background: ${p.theme.colors.cardBgColor};
      box-shadow: 0 -8px 24px rgba(13, 20, 78, 0.05);
    `};
`;

export const Tag = styled(PairButton)<{
  $selected?: boolean;
  $isInvalid?: boolean;
}>`
  border-radius: ${(p) =>
    p.theme.isNewLook ? `${p.theme.radii.pill}px` : "6px"};
  ${(p) =>
    p.$isInvalid &&
    `
    opacity: 0.4;
    pointer-events: none;
  `};
`;
